import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    FaArrowLeft, FaPlus, FaEye, FaEyeSlash, FaTools, FaTrash, FaTimes,
    FaGlobe, FaLayerGroup, FaCheckCircle,
} from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { useTeacherTaxonomy } from "../../hooks/useTeacherTaxonomy";
import {
    ContentThumb, typeMeta, SubjectPicker, ClassPills, TypePicker, TopicSelect, SubjectIcon,
    ProfileSetupNotice,
} from "../../components/marketplace";

// Unified teacher "Content" hub — the list page. A piece of content is a lesson
// (subject/class/topic split into segments) with a visibility: `students`
// (free, only linked students) or `public` (sold via the marketplace). This
// replaces the old separate Lesson Manager + Marketplace pages; the Profile page
// (price + subscribers + what-you-teach) is a sibling route.

const EMPTY_FORM = { title: "", subject: "", class: "", topic: "", type: "lesson_notes" };

// Shared top switch between the two teacher content pages.
export const ContentTabs = ({ active, navigate }) => (
    <div className="flex gap-1 bg-white/10 rounded-full p-1">
        {[["lessons", "Lessons", "/teacher/lessons"], ["marketplace", "Marketplace", "/teacher/marketplace"]].map(
            ([key, label, path]) => (
                <button
                    key={key}
                    onClick={() => navigate(path)}
                    className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                        active === key ? "bg-white text-blue-700" : "text-white/90 hover:bg-white/10"
                    }`}
                >
                    {label}
                </button>
            )
        )}
    </div>
);

const TeacherContent = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);

    // Subject/class options come from the teacher's own profile, not the whole
    // question bank; topics stay global-per-subject (shared across classes).
    const { subjects, classes, topicsBySubject, hasSubjects, hasClasses, needsSetup } = useTeacherTaxonomy();

    const load = async () => {
        setLoading(true);
        try {
            const lRes = await authFetch("/api/lessons/mine");
            const lData = await lRes.json().catch(() => ({}));
            setItems(lData.data || []);
        } catch (err) {
            toast.error("Could not load your content");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (needsSetup) {
            toast.error("Set up the subjects and classes you teach first");
            return;
        }
        if (!form.title.trim() || !form.subject.trim() || !form.class.trim()) {
            toast.error("Title, subject and class are required");
            return;
        }
        setSaving(true);
        try {
            const res = await authFetch("/api/lessons", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't create content");
            toast.success("Draft created — add its segments");
            setForm(EMPTY_FORM);
            setShowForm(false);
            navigate(`/teacher/lessons/${data.data.id}/build`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (item) => {
        const action = item.status === "published" ? "unpublish" : "publish";
        setBusyId(item.id);
        try {
            const res = await authFetch(`/api/lessons/${item.id}/${action}`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || `Failed to ${action}`);
            toast.success(`Content ${action === "publish" ? "published" : "unpublished"}`);
            load();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
        setBusyId(item.id);
        try {
            const res = await authFetch(`/api/lessons/${item.id}`, { method: "DELETE" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Failed to delete");
            toast.success("Content deleted");
            load();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-montserrat">
            <nav className="bg-[#2148C0] shadow-md px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => navigate("/teacher-dashboard")} className="text-white shrink-0"><FaArrowLeft size={18} /></button>
                    <h1 className="text-lg sm:text-xl font-bold text-white shrink-0">Lessons</h1>
                    <div className="hidden sm:block"><ContentTabs active="lessons" navigate={navigate} /></div>
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="bg-white text-blue-700 px-3 sm:px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm shrink-0">
                    <FaPlus /> <span className="hidden sm:inline">New Content</span>
                </button>
            </nav>
            <div className="sm:hidden bg-[#2148C0] px-4 pb-3"><ContentTabs active="lessons" navigate={navigate} /></div>

            <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
                {showForm && (
                    <form onSubmit={handleCreate} className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
                        <div className="flex items-center justify-between">
                            <h2 className="font-semibold text-gray-800">New lesson</h2>
                            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                        </div>
                        {needsSetup && (
                            <ProfileSetupNotice
                                missingSubjects={!hasSubjects}
                                missingClasses={!hasClasses}
                                onSetup={() => navigate("/teacher/marketplace/profile")}
                            />
                        )}
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Title <span className="text-red-500">*</span></label>
                            <input required placeholder="e.g. Photosynthesis explained" className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <SubjectPicker label="Subject" required allowCustom={false} subjects={subjects} value={form.subject}
                                onChange={(v) => setForm({ ...form, subject: v })} />
                            <ClassPills label="Class" required classes={classes} value={form.class}
                                onChange={(v) => setForm({ ...form, class: v })} />
                        </div>
                        <TopicSelect label="Topic (optional)" hint="Pulled from your question bank for the chosen subject — the same topics apply across every class." subject={form.subject}
                            topicsBySubject={topicsBySubject} value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} />
                        <TypePicker label="Content type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
                        <button disabled={saving || needsSetup} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2">
                            <FaPlus size={13} /> {saving ? "Creating…" : "Create & add segments"}
                        </button>
                    </form>
                )}

                {loading && <p className="text-gray-500">Loading…</p>}
                {!loading && items.length === 0 && (
                    <div className="text-center py-16">
                        <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto"><FaLayerGroup size={22} /></div>
                        <p className="mt-3 text-gray-600 font-medium">No content yet</p>
                        <p className="text-sm text-gray-400">Create your first lesson — then split it into segments and add topics.</p>
                    </div>
                )}

                <div className="space-y-3">
                    {items.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-sm p-4">
                            <div className="flex justify-between items-start gap-3">
                                <button onClick={() => navigate(`/teacher/lessons/${item.id}/build`)} className="flex gap-3 min-w-0 text-left">
                                    <div className="w-28 sm:w-36 shrink-0">
                                        <ContentThumb type={item.type} thumbnailUrl={item.thumbnail_url} title={item.title} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-gray-800">{item.title}</h3>
                                            <StatusBadge status={item.status} />
                                            {item.in_marketplace && <MarketplaceBadge />}
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5 flex-wrap">
                                            <SubjectIcon subject={item.subject} size={22} iconSize={11} />
                                            <span className="font-medium">{item.subject}</span>
                                            {item.class && <><span className="text-gray-300">·</span><span className="text-gray-500">{item.class}</span></>}
                                            {item.topic && <><span className="text-gray-300">·</span><span className="text-gray-500">{item.topic}</span></>}
                                        </p>
                                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-1.5 flex-wrap">
                                            <span className="inline-flex items-center gap-1 text-gray-500 font-medium">{typeMeta(item.type).label}</span>
                                            <span className="inline-flex items-center gap-1"><FaLayerGroup size={10} /> {item.segmentCount || 0} segment{item.segmentCount === 1 ? "" : "s"}</span>
                                            <span className="inline-flex items-center gap-1"><FaEye size={10} /> {item.openedCount || 0} opened</span>
                                            <span className="inline-flex items-center gap-1"><FaCheckCircle size={10} /> {item.completedCount || 0} completed</span>
                                        </div>
                                    </div>
                                </button>
                                <div className="flex flex-col items-end gap-2 shrink-0">
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => navigate(`/teacher/lessons/${item.id}/build`)} title="Edit / build" className="text-gray-500 hover:text-blue-600 p-1.5"><FaTools size={15} /></button>
                                        <button onClick={() => toggleStatus(item)} disabled={busyId === item.id} title={item.status === "published" ? "Unpublish" : "Publish"} className="text-gray-500 hover:text-blue-600 p-1.5 disabled:opacity-50">
                                            {item.status === "published" ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                                        </button>
                                        <button onClick={() => handleDelete(item)} disabled={busyId === item.id} title="Delete" className="text-gray-400 hover:text-red-500 p-1.5 disabled:opacity-50"><FaTrash size={14} /></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const StatusBadge = ({ status }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full ${status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
        {status}
    </span>
);

// Small badge shown on a lesson that's also listed on the paid marketplace.
export const MarketplaceBadge = () => (
    <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-amber-100 text-amber-700">
        <FaGlobe size={9} /> In marketplace
    </span>
);

export default TeacherContent;
